import { useState, useMemo } from 'react';
import { Plus, Search, MoreVertical, UserPlus, Pencil, Trash2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useUsers, useDeleteUser } from '@/hooks/useUsers';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { InviteUserModal } from '@/components/settings/InviteUserModal';
import { EditUserModal } from '@/components/settings/EditUserModal';
import { ROLE_LABELS, type AppUser } from '@/types';
import { initials } from '@/lib/utils';

const roleTone = {
  owner:      'brass',
  manager:    'info',
  accounting: 'info',
  sales:      'neutral',
  production: 'neutral',
  staff:      'neutral',
} as const;

export function UsersSettings() {
  const { user: me } = useAuth();
  const { data: users, isLoading, error } = useUsers();
  const deleteUser = useDeleteUser();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editing, setEditing] = useState<AppUser | null>(null);
  const [deleting, setDeleting] = useState<AppUser | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!users) return [];
    const q = query.toLowerCase().trim();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        u.displayName?.toLowerCase().includes(q) ||
        ROLE_LABELS[u.role].toLowerCase().includes(q),
    );
  }, [users, query]);

  if (isLoading) return <LoaderScreen />;

  if (error) {
    return (
      <Card>
        <EmptyState
          icon={ShieldAlert}
          title="Could not load users"
          description={(error as { message?: string })?.message ?? 'An unexpected error occurred.'}
        />
      </Card>
    );
  }

  async function onConfirmDelete() {
    if (!deleting) return;
    try {
      await deleteUser.mutateAsync(deleting.uid);
      toast.success(`Deleted ${deleting.displayName ?? deleting.email}`);
      setDeleting(null);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Failed to delete user.');
    }
  }

  const ownerCount = users?.filter((u) => u.role === 'owner' && !u.disabled).length ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="kicker mb-2">Team · {users?.length ?? 0} members</div>
          <h2 className="font-display text-2xl text-text">Users & Access</h2>
        </div>
        <Button leftIcon={<Plus size={14} />} onClick={() => setInviteOpen(true)}>
          Invite User
        </Button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-sm overflow-hidden border border-border">
        <Stat label="Total" value={users?.length ?? 0} />
        <Stat label="Owners" value={ownerCount} accent={ownerCount < 2} />
        <Stat label="Active" value={users?.filter((u) => !u.disabled).length ?? 0} />
        <Stat label="Disabled" value={users?.filter((u) => u.disabled).length ?? 0} />
      </div>

      {/* Filter */}
      <div className="max-w-sm">
        <Input
          placeholder="Search by name, email, or role…"
          leftIcon={<Search size={14} />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        {filtered.length === 0 ? (
          <EmptyState
            icon={UserPlus}
            title={query ? 'No users match your search' : 'No team members yet'}
            description={query ? 'Try a different search term.' : 'Invite the first member to get started.'}
            action={!query && (
              <Button leftIcon={<Plus size={14} />} onClick={() => setInviteOpen(true)}>
                Invite User
              </Button>
            )}
          />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="kicker px-6 py-3 font-normal">User</th>
                <th className="kicker px-6 py-3 font-normal">Role</th>
                <th className="kicker px-6 py-3 font-normal">Department</th>
                <th className="kicker px-6 py-3 font-normal">Status</th>
                <th className="kicker px-6 py-3 font-normal w-16" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const isMe = me?.uid === u.uid;
                return (
                  <tr key={u.uid} className="border-b border-border last:border-0 hover:bg-bg-hover/40 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 shrink-0 rounded-sm bg-walnut-700 flex items-center justify-center font-display text-xs text-brass-200">
                          {initials(u.displayName || u.email)}
                        </div>
                        <div className="min-w-0">
                          <div className="text-text truncate flex items-center gap-2">
                            {u.displayName || u.email.split('@')[0]}
                            {isMe && <Badge tone="brass">You</Badge>}
                          </div>
                          <div className="text-xs text-text-dim truncate">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge tone={roleTone[u.role]}>{ROLE_LABELS[u.role]}</Badge>
                    </td>
                    <td className="px-6 py-4 text-text-muted">
                      {u.department || <span className="text-text-dim">—</span>}
                    </td>
                    <td className="px-6 py-4">
                      {u.disabled ? (
                        <Badge tone="danger">Disabled</Badge>
                      ) : (
                        <span className="inline-flex items-center gap-2 text-xs text-text-muted">
                          <span className="h-1.5 w-1.5 rounded-full bg-success" />
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right relative">
                      <button
                        onClick={() => setOpenMenu(openMenu === u.uid ? null : u.uid)}
                        className="p-1.5 text-text-dim hover:text-text hover:bg-bg-hover rounded-sm transition-colors"
                        aria-label="Actions"
                      >
                        <MoreVertical size={15} />
                      </button>
                      {openMenu === u.uid && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setOpenMenu(null)}
                            aria-hidden
                          />
                          <div className="absolute right-4 top-12 z-20 w-44 bg-bg-raised border border-border-strong rounded-sm shadow-raised py-1 animate-fade-in">
                            <MenuItem
                              icon={Pencil}
                              onClick={() => { setEditing(u); setOpenMenu(null); }}
                            >
                              Edit details
                            </MenuItem>
                            <div className="my-1 border-t border-border" />
                            <MenuItem
                              icon={Trash2}
                              danger
                              disabled={isMe}
                              onClick={() => { setDeleting(u); setOpenMenu(null); }}
                            >
                              {isMe ? 'Cannot delete self' : 'Delete user'}
                            </MenuItem>
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <p className="text-xs text-text-dim leading-relaxed max-w-2xl">
        Disabling an account preserves the profile and audit history but blocks sign-in. Deleting is permanent and removes
        both the Auth account and the profile. Owners cannot be demoted, disabled, or deleted if they are the last active owner.
      </p>

      <InviteUserModal open={inviteOpen} onClose={() => setInviteOpen(false)} />
      <EditUserModal open={!!editing} onClose={() => setEditing(null)} user={editing} />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={onConfirmDelete}
        loading={deleteUser.isPending}
        destructive
        title={`Delete ${deleting?.displayName ?? deleting?.email}?`}
        confirmLabel="Delete user"
        description={
          <>
            This will permanently remove the Auth account and profile for{' '}
            <span className="text-text">{deleting?.email}</span>. Their audit history is retained. This action cannot be undone.
            <br /><br />
            Consider <span className="text-text">disabling</span> the account instead.
          </>
        }
      />
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className="bg-bg-panel p-4">
      <div className="kicker mb-2">{label}</div>
      <div className={`font-display text-2xl tabular ${accent ? 'text-warning' : 'text-text'}`}>{value}</div>
    </div>
  );
}

function MenuItem({
  icon: Icon, children, onClick, danger, disabled,
}: {
  icon: typeof Pencil;
  children: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        w-full flex items-center gap-2.5 px-3 py-2 text-xs text-left transition-colors
        disabled:opacity-40 disabled:cursor-not-allowed
        ${danger ? 'text-danger hover:bg-danger/10' : 'text-text hover:bg-bg-hover'}
      `}
    >
      <Icon size={13} />
      {children}
    </button>
  );
}
